import { CompozzError, isCompozzError } from '@dto/CompozzError';
import { isLoginResponse, JwtClaims, LoginRequest, LoginResponse } from '@dto/login';
import bgImage from '@img/BG_Compozz4b.jpg';
import logo_fr from '@img/Compozz_vlogo_app_violet.svg';
import logo_en from '@img/Compozz_vlogo_app_violet_EN.svg';
import '@login/Login.css';
import { BASE_URL } from '@utils/constants';
import { TW_ErrorMessage, TW_LoginBackground, TW_LoginForm, TW_LoginForm_Button, TW_LoginForm_Container, TW_LoginForm_Input, TW_LoginForm_PasswordToggle, TW_LoginForm_PasswordWrapper, TW_LoginLogo } from '@utils/twClasses';
import { MAX_RETRY, preventDefault, simplePost } from '@utils/utils';
import { Eye, EyeSlash } from 'iconsax-react';
import { jwtDecode } from 'jwt-decode';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PREFERRED_LANG_KEY, translate } from 'src/i18n/i18n';
import { useModelStore, useUserStore } from 'src/store';

function Login() {

    const { t } = useTranslation();
    // Temporary state for input fields
    const [inputUsername, setInputUsername] = useState('');
    const [inputPassword, setInputPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');

    // Update user store
    const updateUsername = useUserStore(state => state.updateUsername);
    const updateOrgId = useUserStore(state => state.updateOrgId);
    const updateUserRole = useModelStore(state => state.updateUserRole);

    const userLang = localStorage.getItem(PREFERRED_LANG_KEY) ?? navigator.language;
    const logo = userLang.startsWith('fr') ? logo_fr : logo_en;
    // const logo_purple = userLang.startsWith('fr') ? logo_purple_fr : logo_purple_en;
    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            const loginRequest: LoginRequest = {
                username: inputUsername.toLowerCase(),
                password: inputPassword
            }
            simplePost<LoginResponse>(BASE_URL + '/auth/login', loginRequest, MAX_RETRY).then(data => {
                if (data){
                    if (isLoginResponse(data)) {
                        const decoded = jwtDecode<JwtClaims>(data.access_token);
                        const orgId = Object.keys(decoded.organization)[0];
                        updateUsername(inputUsername);
                        updateOrgId(orgId);
                        window.location.href = '/';
                    } else {
                        if (isCompozzError(data)) {
                            const cError = data as CompozzError;
                            let errorMessage = '';
                            let logError = '';
                            if (cError.i18n) {
                                errorMessage = t('compozz.internal.error');
                                logError = translate(t, cError.i18n, cError.params);
                                if (cError.code === 402 || cError.code === 401 || cError.code === 403) {
                                    errorMessage = logError;
                                }
                            } else {
                                errorMessage = t('connection.invalid.credentials');
                                logError = errorMessage;
                            }
                            updateUsername(undefined);
                            updateOrgId(undefined);
                            updateUserRole(null);
                            console.error('logError', logError);
                            setError(errorMessage);
                        } else {
                            updateUsername(undefined);
                            updateOrgId(undefined);
                            updateUserRole(null);
                            setError(t('connection.invalid.credentials'));
                        }
                    }
                } else {
                    updateUsername(undefined);
                    updateOrgId(undefined);
                    updateUserRole(null);
                    setError(t('connection.invalid.credentials'));
                }
            }).catch(error => {
                console.error('Error checking login status:', error);
                updateUsername(undefined);
                updateOrgId(undefined);
                updateUserRole(null);
                setError(t('connection.invalid.credentials'));
            });
        } catch (error) {
            console.error('error', error);
            updateUsername(undefined);
            updateOrgId(undefined);
            updateUserRole(null);
        }
    };


    const onUsernameChange = (value: string) => {
        setInputUsername(value);
        setError('');
    }

    const onPasswordChange = (value: string) => {
        setInputPassword(value);
        setError('');
    }

    return (
        <div className={TW_LoginBackground} style={{ backgroundImage: `url(${bgImage})` }}>
            <div className={TW_LoginForm}>
                <img src={logo} alt="Compozz Logo" className={TW_LoginLogo} width={1154} height={342}/>
                <p className={TW_ErrorMessage + " text-xs"} tabIndex={-1}>{error}</p>
                <form className={TW_LoginForm_Container} onSubmit={preventDefault}>
                    <input
                        type="text"
                        value={inputUsername}
                        onChange={(e) => onUsernameChange(e.target.value)}
                        placeholder={t("connection.input.username.placeholder")} 
                        tabIndex={1}
                        className={TW_LoginForm_Input}/>
                    <div className={TW_LoginForm_PasswordWrapper}>
                        <input
                            type={showPassword ? "text" : "password"}
                            value={inputPassword}
                            onChange={(e) => onPasswordChange(e.target.value)}
                            placeholder={t("connection.input.password.placeholder")}
                            tabIndex={2}
                            className={TW_LoginForm_Input + " pr-8"}/>
                        {/* type="button" so Enter in the field still triggers the submit button */}
                        <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowPassword(v => !v)}
                            aria-label={t(showPassword ? "connection.input.password.hide" : "connection.input.password.show")}
                            title={t(showPassword ? "connection.input.password.hide" : "connection.input.password.show")}
                            className={TW_LoginForm_PasswordToggle}>
                            {showPassword ? <EyeSlash size={18}/> : <Eye size={18}/>}
                        </button>
                    </div>
                    <button 
                        type="submit"
                        tabIndex={3}
                        onClick={handleSubmit}
                        className={TW_LoginForm_Button}>
                        {t("connection.button.ok.text")}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default Login;